import { useI18n } from '@/hooks/useI18n';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorInline } from '@/components/primitives/ErrorInline';
import { Chip } from '@/components/primitives/Chip';
import { Stagger } from '@/components/primitives/Stagger';
import { useAccount } from '@/hooks/queries/useAccounts';
import { useAccountDashboard } from '@/hooks/queries/useAccountDashboard';
import { useIdentities } from '@/hooks/queries/useIdentities';
import { useCheckNow } from '@/hooks/mutations/useCheckNow';
import { useDeleteAccount } from '@/hooks/mutations/useAccountMutations';
import { usePeriodSelection } from '@/hooks/usePeriodSelection';
import { formatNumber, formatPeriodRange, formatRelative } from '@/lib/format';
import { PERIOD_DAYS_OPTIONS } from '@/lib/constants';
import { buildProfileUrl } from '@/lib/ig';
import { proxyImage } from '@/lib/imageProxy';
import { cn } from '@/lib/cn';
import { FollowersViewsChart } from './FollowersViewsChart';
import { ReelsRegistry } from './ReelsRegistry';
import { useAuth } from '@/contexts/AuthContext';
import { canManageWorkspace } from '@/lib/permissions';

export function AccountProfileScreen() {
  const { t } = useI18n();
  const { user } = useAuth();
  const canManage = canManageWorkspace(user?.role);
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [period, setPeriod] = usePeriodSelection(
    'account-profile:periodDays',
    PERIOD_DAYS_OPTIONS,
    30,
  );
  const [deleteOpen, setDeleteOpen] = useState(false);

  const accountQ = useAccount(id);
  const dashboardQ = useAccountDashboard(id, period);
  const identitiesQ = useIdentities();
  const check = useCheckNow();
  const del = useDeleteAccount();

  if (accountQ.error) {
    return (
      <div className="mt-4">
        <ErrorInline error={accountQ.error} onRetry={() => accountQ.refetch()} />
      </div>
    );
  }

  if (accountQ.isLoading || !accountQ.data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-20 w-3/4" />
        <Skeleton className="h-[280px] w-full rounded-[14px]" />
      </div>
    );
  }

  const account = accountQ.data;
  const dashboard = dashboardQ.data;
  const identity = identitiesQ.data?.find((i) => i.id === account.identityId) ?? null;
  const initial = account.username.charAt(0).toUpperCase();
  const delta =
    dashboard && dashboard.followersHistory.points.length > 1
      ? computeFollowerDelta(dashboard)
      : null;

  return (
    <Stagger className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-center gap-2 text-[12px]">
          <Link
            to="/accounts"
            className="flex min-h-11 shrink-0 items-center gap-1 text-text-3 transition-colors hover:text-violet-deep sm:min-h-0"
          >
            <ArrowLeft className="h-3 w-3" />
            {t('Comptes', 'Accounts')}
          </Link>
          <span className="text-text-4">/</span>
          <span className="truncate font-medium text-text-1">@{account.username}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canManage && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => check.mutate(account.id)}
              disabled={check.isPending}
              className="gap-1.5"
            >
              {check.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              {t('Scraper', 'Scrape')}
            </Button>
          )}
          <Button variant="ghost" size="sm" asChild>
            <a
              href={buildProfileUrl(account.username)}
              target="_blank"
              rel="noopener noreferrer"
              className="gap-1.5"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Instagram
            </a>
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setDeleteOpen(true)}
            disabled={del.isPending}
            className="gap-1.5"
          >
            <Trash2 className="h-3.5 w-3.5" />
            {t('Supprimer', 'Delete')}
          </Button>
        </div>
      </div>

      <Dialog open={deleteOpen} onOpenChange={(open) => !del.isPending && setDeleteOpen(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t(`Supprimer @${account.username} ?`, `Delete @${account.username}?`)}
            </DialogTitle>
            <DialogDescription>
              {t(
                'Le compte sera retiré du scraping et de la liste. Les snapshots et reels déjà collectés resteront en base mais ne seront plus mis à jour. Cette action est irréversible.',
                'The account will be removed from scraping and from the list. Existing snapshots and reels will remain in the database but will no longer be updated. This action cannot be undone.',
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setDeleteOpen(false)}
              disabled={del.isPending}
            >
              {t('Annuler', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={del.isPending}
              onClick={() =>
                del.mutate(account.id, {
                  onSuccess: () => {
                    setDeleteOpen(false);
                    navigate('/accounts');
                  },
                })
              }
              className="gap-1.5"
            >
              {del.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
              {t('Supprimer', 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <section className="rounded-[18px] border border-line-1 bg-bg-1 p-4 shadow-card sm:p-6">
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-6">
          <Avatar className="h-16 w-16 shrink-0 ring-2 ring-violet/25 sm:h-20 sm:w-20">
            {dashboard?.profilePictureUrl ? (
              <AvatarImage src={proxyImage(dashboard.profilePictureUrl)} alt={account.username} />
            ) : null}
            <AvatarFallback className="bg-violet-soft text-2xl font-semibold text-violet-deep">
              {initial}
            </AvatarFallback>
          </Avatar>

          <div className="w-full min-w-0 flex-1 space-y-1.5">
            <h1 className="font-display break-words text-[22px] font-semibold tracking-tight text-text-1 sm:text-[26px]">
              @{account.username}
            </h1>
            <div className="flex flex-wrap items-center gap-2">
              {identity && (
                <Chip tone="violet">
                  <span
                    className="mr-1 h-1.5 w-1.5 rounded-full"
                    style={{ background: identity.color ?? 'hsl(var(--text-3))' }}
                  />
                  {identity.name}
                </Chip>
              )}
              <Chip tone={account.isActive ? 'emerald' : 'default'}>
                {account.isActive ? t('Actif', 'Active') : t('Inactif', 'Inactive')}
              </Chip>
            </div>
            {dashboard?.bio && (
              <p className="max-w-xl break-words text-[13px] text-text-2">{dashboard.bio}</p>
            )}
            {dashboard?.externalLink && (
              <a
                href={dashboard.externalLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex max-w-full items-start gap-1 text-[13px] font-medium text-violet-deep transition-colors hover:text-violet"
              >
                <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span className="min-w-0 break-all">{formatLinkLabel(dashboard.externalLink)}</span>
              </a>
            )}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
          <StatBlock
            label={t('Abonnés', 'Followers')}
            value={
              dashboard?.followersCount !== undefined ? formatNumber(dashboard.followersCount) : '—'
            }
            delta={delta}
            loading={dashboardQ.isLoading}
            primary
          />
          <StatBlock
            label={t('Publications', 'Posts')}
            value={dashboard?.mediaCount !== undefined ? formatNumber(dashboard.mediaCount) : '—'}
            loading={dashboardQ.isLoading}
          />
          <StatBlock
            label={t('Reels viraux', 'Viral reels')}
            value={formatNumber(dashboard?.recentViralReels.length ?? 0)}
            loading={dashboardQ.isLoading}
          />
          <StatBlock
            label={t('Dernier scrape', 'Last scrape')}
            value={formatRelative(account.lastCheckedAt)}
            small
          />
        </div>
      </section>

      <section className="min-w-0 rounded-[18px] border border-line-1 bg-bg-1 p-3 shadow-card sm:p-5">
        <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-[15px] font-semibold tracking-tight text-text-1">
              {t('Évolution des abonnés et des vues', 'Follower and view trends')}
            </h2>
            <p className="text-[12px] text-text-3 first-letter:uppercase">
              {formatPeriodRange(period)}
            </p>
          </div>
          <div
            role="group"
            aria-label={t('Période', 'Period')}
            className="flex flex-wrap items-center gap-1 rounded-[10px] bg-bg-2 p-1"
          >
            {PERIOD_DAYS_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={period === option.value}
                onClick={() => setPeriod(option.value)}
                className={cn(
                  'min-h-11 rounded-[7px] px-2.5 py-1 text-[12px] font-medium transition-colors sm:min-h-0',
                  period === option.value
                    ? 'bg-bg-1 text-text-1 shadow-[0_1px_2px_hsl(240_10%_12%/0.06)]'
                    : 'text-text-3 hover:text-text-1',
                )}
              >
                {t(option.label, option.labelEn)}
              </button>
            ))}
          </div>
        </header>
        <FollowersViewsChart
          followers={dashboard?.followersHistory}
          views={dashboard?.viewsHistory}
          loading={dashboardQ.isLoading}
        />
      </section>

      <ReelsRegistry accountId={account.id} />
    </Stagger>
  );
}

function StatBlock({
  label,
  value,
  delta,
  loading,
  primary,
  small,
}: {
  label: string;
  value: string;
  delta?: number | null;
  loading?: boolean;
  primary?: boolean;
  small?: boolean;
}) {
  const { locale } = useI18n();
  return (
    <div className="min-w-0 rounded-[12px] border border-line-1 bg-bg-0/60 p-3 sm:p-4">
      <div className="text-[11px] font-medium text-text-3">{label}</div>
      {loading ? (
        <Skeleton className="mt-1 h-8 w-20" />
      ) : (
        <div
          className={cn(
            'font-display mt-1 break-words tabular-nums tracking-tight text-text-1',
            primary
              ? 'text-[24px] leading-none sm:text-[28px]'
              : small
                ? 'text-[16px]'
                : 'text-[22px] leading-none',
          )}
        >
          {value}
          {delta !== null && delta !== undefined && (
            <span
              className={cn(
                'mt-1 block text-[12px] font-semibold leading-normal sm:ml-2 sm:mt-0 sm:inline sm:align-middle',
                delta >= 0 ? 'text-emerald' : 'text-rose',
              )}
            >
              {delta >= 0 ? '+' : ''}
              {(delta * 100).toLocaleString(locale, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}
              %
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function formatLinkLabel(url: string): string {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, '');
    const path = parsed.pathname.replace(/\/+$/, '');
    return path && path !== '/' ? `${host}${path}` : host;
  } catch {
    return url;
  }
}

function computeFollowerDelta(
  dashboard: { followersHistory: { points: { value: number | null }[] } } | undefined,
): number | null {
  const points = dashboard?.followersHistory?.points ?? [];
  const first = points.find((p) => p.value !== null);
  const last = [...points].reverse().find((p) => p.value !== null);
  if (!first?.value || !last?.value || first.value === 0) return null;
  return (last.value - first.value) / first.value;
}
